import { environment } from "../../../environments/environment";
import { onlineTileUrl } from "./webgl-maplibre.online-tiles";
import {
  OBSTACLE_ICON_SIZE,
  OBSTACLE_MIN_ZOOM,
  addObstacleImages,
  obstacleImageExpression,
} from "./webgl-maplibre.obstacle-symbols";
import { aeroTileUrl } from "./webgl-maplibre.aero-tiles";
import { miscSymbolImage } from "./webgl-maplibre.misc-symbols";
import { radioNavaidImage } from "./webgl-maplibre.radio-navaids";
import { reportingPointImage } from "./webgl-maplibre.reporting-points";
import { Map as MlMap, GeoJSONSource, LayerSpecification, ExpressionSpecification } from "maplibre-gl";
import { emptyFC } from "./webgl-maplibre.helpers";
import { MapConfig, RasterLayerConfig } from "./webgl-maplibre.types";

// --------------- raster layers ---------------

// Paint order: later entries draw on top. orthoBy sits above the Vienna ortho
// (wins where both cover). Optional BEV charts sit below the aero overlay.
const RASTER_KEYS = ["base", "ortho", "orthoBy", "bev", "aero"] as const;

/** Rasters that are the map picture itself. The aero overlay carries labels, so it is not one. */
const BASEMAP_LAYER_IDS = new Set(["base", "ortho", "orthoBy", "bev"].map((key) => `${key}-layer`));

/** Where a terrain tint goes: above the basemap, below every layer that is not terrain. */
export function firstLayerAboveBasemap(map: MlMap): string | undefined {
  return map
    .getStyle()
    .layers.find(
      (l) =>
        l.type !== "background" && l.type !== "hillshade" && l.type !== "color-relief" && !BASEMAP_LAYER_IDS.has(l.id)
    )?.id;
}

export function chartTransitionZoom(config: MapConfig): number {
  return config.aero?.url
    ? Math.min(config.aero.layerMaxZoom, config.vectorLabelMinZoom ?? 24)
    : (config.vectorLabelMinZoom ?? 0);
}

function addRasterSource(map: MlMap, id: string, layerConfig: RasterLayerConfig, tileBaseUrl: string): void {
  const urlTemplate = layerConfig.url.startsWith("https://") ? layerConfig.url : `${tileBaseUrl}/${layerConfig.url}`;

  map.addSource(id, {
    type: "raster",
    tiles: [id === "aero-tiles" ? aeroTileUrl(urlTemplate) : onlineTileUrl(urlTemplate)],
    tileSize: 256,
    bounds: layerConfig.bounds,

    // lowest source zoom that exists
    minzoom: layerConfig.zoomLevels[0],
    // highest source zoom that exists
    maxzoom: layerConfig.zoomLevels[layerConfig.zoomLevels.length - 1],
  });
}

// --------------- attribution (CC-BY credits) ---------------

export interface AttributionLayer {
  attribution: string;
  bounds?: [number, number, number, number];
  minZoom: number;
}

/** Credit-bearing raster layers, in paint order. */
export function collectAttributionLayers(mapConfig: MapConfig): AttributionLayer[] {
  const out: AttributionLayer[] = [];
  for (const key of RASTER_KEYS) {
    const cfg = mapConfig[key];
    if (cfg?.attribution) out.push({ attribution: cfg.attribution, bounds: cfg.bounds, minZoom: cfg.layerMinZoom });
  }
  return out;
}

/** Credits for the imagery under the map centre: EVERY raster layer whose zoom
 *  is in range and whose bounds contain the centre, topmost first.
 *
 *  Not topmost-only. The bounding rectangles cannot trace the AT/DE border, so
 *  they overlap on roughly lon 9.4-13.9 / lat 47.2-49.1 - a band containing
 *  Innsbruck, Salzburg, Kufstein and Bregenz. Topmost-wins credited Bayern
 *  across all of it, where Bayern has no imagery and basemap.at is what
 *  actually paints: the wrong body credited AND the right one omitted, which is
 *  the half of CC-BY that is an actual violation.
 *
 *  Crediting both inside the overlap is the deliberate trade: a redundant
 *  credit is not a licence breach, a missing one is. The residual is that a
 *  viewport whose CENTRE sits just outside a layer's bounds while its imagery
 *  is still visible at the edge stays uncredited - closing that needs
 *  map.getBounds() intersection rather than a point test.
 *
 *  `center` is [lon, lat]; bounds are [west, south, east, north]. */
export function attributionsInView(layers: AttributionLayer[], center: [number, number], zoom: number): string[] {
  const [lon, lat] = center;
  const out: string[] = [];
  for (let i = layers.length - 1; i >= 0; i--) {
    const l = layers[i];
    if (zoom < l.minZoom) continue;
    if (l.bounds) {
      const [bw, bs, be, bn] = l.bounds;
      if (lon < bw || lon > be || lat < bs || lat > bn) continue; // centre outside bounds
    }
    if (!out.includes(l.attribution)) out.push(l.attribution);
  }
  return out;
}

export function addRasterLayers(map: MlMap, mapConfig: MapConfig, tileBaseUrl: string): void {
  for (const key of RASTER_KEYS) {
    const cfg = mapConfig[key];
    // A merged configprocess blob may omit a key (older device cache) or null
    // it out; addRasterSource dereferences cfg.url unconditionally, and a throw
    // here is inside map.on("load") where nothing catches it — the whole map
    // would stay a grey background. Skip the layer instead.
    if (!cfg?.url) continue;
    const sourceId = `${key}-tiles`;
    const layerId = `${key}-layer`;

    addRasterSource(map, sourceId, cfg, tileBaseUrl);
    map.addLayer({
      id: layerId,
      type: "raster",
      source: sourceId,
      ...(key === "aero" ? { paint: { "raster-fade-duration": 0 } } : {}),
      minzoom: cfg.layerMinZoom,
      maxzoom:
        key === "aero" && mapConfig.vectorLabelMinZoom !== undefined
          ? chartTransitionZoom(mapConfig)
          : cfg.layerMaxZoom,
    });
  }
}

// --------------- vector source & layers ---------------

/** Feature types drawn over the aviation chart rather than by it: webcams and weather. */
const OVERLAY_OBJ_TYPES = [4, 5];
/** Opacity of the whole webcam cone layer; the feature colour alpha is ignored. */
const WEBCAM_FILL_OPACITY = 0.25;

export function addVectorSourceAndLayers(map: MlMap, labelMinZoom = 0): void {
  const addLayer = (layer: LayerSpecification): void => {
    map.addLayer({ ...layer, minzoom: Math.max(layer.minzoom ?? 0, labelMinZoom) });
  };
  map.addSource("airspace-labels", { type: "geojson", data: emptyFC() });
  map.addSource("vectormap", {
    type: "geojson",
    data: emptyFC(),
  });

  const vectorLayers: Extract<
    LayerSpecification,
    { type: "fill" | "fill-extrusion" | "line" | "circle" | "symbol" }
  >[] = [];

  // 1. Polygon fill
  vectorLayers.push({
    id: "vm-polygon-fill",
    type: "fill",
    source: "vectormap",
    filter: ["any", ["==", ["geometry-type"], "Polygon"], ["==", ["geometry-type"], "MultiPolygon"]],
    layout: { "fill-sort-key": ["coalesce", ["get", "zIndex"], 0] },
    paint: {
      "fill-color": ["coalesce", ["get", "fillColor"], "rgba(0,0,0,0)"],
      "fill-opacity": ["step", ["zoom"], 1, 15, ["case", ["==", ["get", "heliportSector"], true], 0, 1]],
    },
  });

  // Webcam cones: a flat fill-extrusion, not a fill. Its opacity applies to the
  // whole layer and a stencil pass paints each pixel once, so overlapping cones
  // do not add up to a darker patch. The colour is made opaque for that reason.
  vectorLayers.push({
    id: "vm-webcam-fill",
    type: "fill-extrusion",
    source: "vectormap",
    filter: [
      "all",
      ["==", ["get", "_objType"], 4],
      ["any", ["==", ["geometry-type"], "Polygon"], ["==", ["geometry-type"], "MultiPolygon"]],
    ],
    paint: {
      "fill-extrusion-color": [
        "let",
        "c",
        ["to-rgba", ["to-color", ["coalesce", ["get", "fillColor"], "#808080"]]],
        ["rgb", ["at", 0, ["var", "c"]], ["at", 1, ["var", "c"]], ["at", 2, ["var", "c"]]],
      ],
      "fill-extrusion-opacity": WEBCAM_FILL_OPACITY,
      "fill-extrusion-height": 0,
      "fill-extrusion-base": 0,
    },
  });

  // Polygon line-offset is positive inward. Half the band width keeps its
  // outside edge on the boundary; draw the original crisp outline afterwards.
  vectorLayers.push({
    id: "vm-airspace-inner-stroke",
    type: "line",
    source: "vectormap",
    filter: [
      "all",
      ["==", ["get", "_objType"], 0],
      ["any", ["==", ["geometry-type"], "Polygon"], ["==", ["geometry-type"], "MultiPolygon"]],
    ],
    layout: {
      "line-sort-key": ["coalesce", ["get", "zIndex"], 0],
      "line-join": "miter",
      "line-miter-limit": 5,
    },
    paint: {
      "line-color": ["coalesce", ["get", "innerStrokeColor"], ["get", "strokeColor"], "#255285"],
      "line-width": ["max", 0, ["coalesce", ["get", "innerStrokeWidth"], 30]],
      "line-offset": ["/", ["max", 0, ["coalesce", ["get", "innerStrokeWidth"], 30]], 2],
      "line-opacity": ["coalesce", ["get", "innerStrokeOpacity"], 0.22],
    },
  });

  // Optional continuous casing around the airspace boundary. haloWidth is
  // the extra width on each side of the line; zero disables it.
  vectorLayers.push({
    id: "vm-airspace-halo",
    type: "line",
    source: "vectormap",
    filter: [
      "all",
      ["==", ["get", "_objType"], 0],
      [">", ["coalesce", ["get", "haloWidth"], 0], 0],
      ["any", ["==", ["geometry-type"], "Polygon"], ["==", ["geometry-type"], "MultiPolygon"]],
    ],
    layout: {
      "line-sort-key": ["coalesce", ["get", "zIndex"], 0],
      "line-join": "miter",
      "line-miter-limit": 5,
    },
    paint: {
      "line-color": ["coalesce", ["get", "haloColor"], "#ffffff"],
      "line-width": [
        "+",
        ["coalesce", ["get", "strokeWidth"], 1],
        ["*", 2, ["max", 0, ["coalesce", ["get", "haloWidth"], 0]]],
      ],
    },
  });

  // 2. Solid lines (no lineDash property)
  vectorLayers.push({
    id: "vm-line",
    type: "line",
    source: "vectormap",
    filter: [
      "all",
      ["!", ["has", "lineDash"]],
      ["!=", ["get", "runwayCenterline"], true],
      [
        "any",
        ["==", ["geometry-type"], "LineString"],
        ["==", ["geometry-type"], "MultiLineString"],
        ["==", ["geometry-type"], "Polygon"],
        ["==", ["geometry-type"], "MultiPolygon"],
      ],
    ],
    layout: {
      "line-sort-key": ["coalesce", ["get", "zIndex"], 0],
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": ["coalesce", ["get", "strokeColor"], "rgb(0,0,0)"],
      "line-width": ["coalesce", ["get", "strokeWidth"], 1],
    },
  });

  // 3. Dashed lines
  vectorLayers.push({
    id: "vm-line-dashed",
    type: "line",
    source: "vectormap",
    filter: [
      "all",
      ["has", "lineDash"],
      [
        "any",
        ["==", ["geometry-type"], "LineString"],
        ["==", ["geometry-type"], "MultiLineString"],
        ["==", ["geometry-type"], "Polygon"],
        ["==", ["geometry-type"], "MultiPolygon"],
      ],
    ],
    layout: {
      "line-sort-key": ["coalesce", ["get", "zIndex"], 0],
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": ["coalesce", ["get", "strokeColor"], ["coalesce", ["get", "dashColor"], "rgb(0,0,0)"]],
      "line-width": ["coalesce", ["get", "strokeWidth"], 1],
      "line-dasharray": ["get", "lineDash"],
    },
  });

  // Ground-length dashes stay legible at close zoom, without becoming a solid speckled line.
  vectorLayers.push({
    id: "vm-runway-centerline",
    type: "line",
    source: "vectormap",
    minzoom: 13,
    filter: ["==", ["get", "runwayCenterline"], true],
    layout: { "line-cap": "butt" },
    paint: {
      "line-color": "#ffffff",
      "line-width": [
        "interpolate",
        ["linear"],
        ["zoom"],
        13,
        1,
        15,
        ["max", 1, ["*", ["get", "widthAtZoom0"], 32768]],
        18,
        ["max", 1, ["*", ["get", "widthAtZoom0"], 262144]],
        20,
        ["max", 1, ["*", ["get", "widthAtZoom0"], 1048576]],
        22,
        ["max", 1, ["*", ["get", "widthAtZoom0"], 4194304]],
        24,
        ["max", 1, ["*", ["get", "widthAtZoom0"], 16777216]],
      ],
    },
  });

  addObstacleImages(map);
  vectorLayers.push({
    id: "vm-obstacle-symbols",
    type: "symbol",
    source: "vectormap",
    filter: ["all", ["==", ["get", "_objType"], 6], ["==", ["geometry-type"], "Point"]],
    layout: {
      "icon-image": obstacleImageExpression,
      "icon-size": OBSTACLE_ICON_SIZE,
      "icon-anchor": "bottom",
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
      "icon-pitch-alignment": "viewport",
      "icon-rotation-alignment": "viewport",
    },
  });

  // 4. Point circles (non-label points)
  vectorLayers.push({
    id: "vm-point-circle",
    type: "circle",
    source: "vectormap",
    filter: [
      "all",
      ["any", ["==", ["geometry-type"], "Point"], ["==", ["geometry-type"], "MultiPoint"]],
      ["!=", ["get", "_isLabel"], true],
      ["!=", ["get", "_objType"], 6],
    ],
    paint: {
      "circle-radius": ["*", 1.4, ["coalesce", ["get", "radius"], 3]],
      "circle-color": ["coalesce", ["get", "fillColor"], "rgb(0,0,0)"],
      "circle-stroke-color": ["coalesce", ["get", "strokeColor"], "rgb(0,0,0)"],
      "circle-stroke-width": ["coalesce", ["get", "strokeWidth"], 2],
    },
  });

  // Stretchable background keeps every label legible over charts and imagery.
  if (!map.hasImage("nav-label-box")) {
    const pixels = new Uint8Array(8 * 8 * 4);
    for (let i = 0; i < pixels.length; i += 4) {
      pixels.set([75, 79, 85, 245], i);
    }
    map.addImage(
      "nav-label-box",
      { width: 8, height: 8, data: pixels },
      {
        stretchX: [[0, 8]],
        stretchY: [[0, 8]],
        content: [0, 0, 8, 8],
      }
    );
  }

  if (!map.hasImage("runway-label-box")) {
    const pixels = new Uint8Array(8 * 8 * 4);
    for (let i = 0; i < pixels.length; i += 4) pixels.set([255, 255, 255, 255], i);
    map.addImage(
      "runway-label-box",
      { width: 8, height: 8, data: pixels },
      {
        stretchX: [[0, 8]],
        stretchY: [[0, 8]],
        content: [0, 0, 8, 8],
      }
    );
  }

  for (const [id, filled] of [
    ["vfr-mrp", true],
    ["vfr-rp", false],
  ] as const) {
    if (!map.hasImage(id)) map.addImage(id, reportingPointImage(filled), { pixelRatio: 2 });
  }
  vectorLayers.push({
    id: "vm-reporting-points",
    type: "symbol",
    source: "vectormap",
    filter: [
      "all",
      ["==", ["get", "_objType"], 16],
      ["match", ["get", "reportingPointType"], ["VFR-MRP", "VFR-RP"], true, false],
    ],
    layout: {
      "icon-image": ["match", ["get", "reportingPointType"], "VFR-MRP", "vfr-mrp", "vfr-rp"],
      "icon-size": 1.4,
      "icon-allow-overlap": true,
      "icon-rotation-alignment": "viewport",
      "icon-pitch-alignment": "viewport",
      "text-field": ["get", "text"],
      "text-font": ["Open Sans Extra Bold"],
      "text-size": 22,
      "text-anchor": "top",
      "text-offset": [0, 1.15],
      "text-optional": true,
      "text-allow-overlap": false,
      "text-padding": 2,
    },
    paint: { "text-color": "#00008b", "text-halo-color": "#ffffff", "text-halo-width": 1.5 },
  });

  for (const kind of ["VOR", "VOR/DME", "DME", "NDB"] as const) {
    if (!map.hasImage(`radio-${kind}`)) map.addImage(`radio-${kind}`, radioNavaidImage(kind), { pixelRatio: 2 });
  }
  vectorLayers.push({
    id: "vm-radio-navaids",
    type: "symbol",
    source: "vectormap",
    filter: [
      "all",
      ["==", ["get", "_objType"], 16],
      ["has", "navaidType"],
      ["!=", ["get", "navaidSymbolHidden"], true],
    ],
    layout: {
      "icon-image": ["concat", "radio-", ["get", "navaidType"]],
      "icon-size": 1.4,
      "icon-allow-overlap": true,
      "icon-rotation-alignment": "viewport",
      "icon-pitch-alignment": "viewport",
      "text-field": [
        "concat",
        ["get", "text"],
        [
          "case",
          ["!=", ["coalesce", ["get", "navaidFrequency"], ""], ""],
          ["concat", "\n", ["get", "navaidFrequency"]],
          "",
        ],
      ],
      "text-font": ["Open Sans Extra Bold"],
      "text-size": 16,
      "text-anchor": "top",
      "text-offset": [0, 1.75],
      "text-optional": true,
      "text-allow-overlap": false,
      "text-padding": 3,
    },
    paint: { "text-color": "#000", "text-halo-color": "#fff", "text-halo-width": 1 },
  });

  for (const kind of ["pass", "ais", "landing-t", "windsock"] as const) {
    if (!map.hasImage(`misc-${kind}`)) map.addImage(`misc-${kind}`, miscSymbolImage(kind), { pixelRatio: 2 });
  }
  vectorLayers.push({
    id: "vm-mountain-passes",
    type: "symbol",
    source: "vectormap",
    filter: ["==", ["get", "mountainPass"], true],
    layout: {
      "icon-image": "misc-pass",
      "icon-size": 1.4,
      "icon-allow-overlap": true,
      "icon-rotate": ["coalesce", ["get", "pointRotation"], 0],
      "icon-rotation-alignment": "map",
      "text-field": ["get", "text"],
      "text-font": ["Open Sans Extra Bold"],
      "text-size": 18,
      "text-anchor": "top",
      "text-offset": [0, 1.6],
      "text-optional": true,
      "text-padding": 3,
    },
    paint: { "text-color": "#000", "text-halo-color": "#fff", "text-halo-width": 1.5 },
  });
  vectorLayers.push({
    id: "vm-ais",
    type: "symbol",
    source: "vectormap",
    minzoom: 15,
    filter: ["==", ["get", "miscSymbol"], "ais"],
    layout: {
      "icon-image": "misc-ais",
      "icon-size": 1.4,
      "icon-allow-overlap": true,
      "icon-anchor": "top",
      "icon-offset": [0, 4],
      "icon-rotation-alignment": "viewport",
      "icon-pitch-alignment": "viewport",
    },
  });

  vectorLayers.push({
    id: "vm-airport-misc-symbols",
    type: "symbol",
    source: "vectormap",
    minzoom: 14,
    filter: [
      "any",
      ["==", ["get", "miscSymbol"], "windsock"],
      ["all", [">=", ["zoom"], 15], ["==", ["get", "miscSymbol"], "landing-t"]],
    ],
    layout: {
      "icon-image": ["concat", "misc-", ["get", "miscSymbol"]],
      "icon-size": 1.4,
      "icon-allow-overlap": true,
      "icon-rotation-alignment": "viewport",
      "icon-pitch-alignment": "viewport",
    },
  });

  // Keep the geographic anchor visible above helicopter point labels.
  vectorLayers.push({
    id: "vm-helicopter-label-points",
    type: "circle",
    source: "vectormap",
    filter: [
      "all",
      ["==", ["geometry-type"], "Point"],
      ["any", ["==", ["get", "fatoLabel"], true], ["==", ["slice", ["coalesce", ["get", "text"], ""], 0, 4], "IAP "]],
    ],
    paint: {
      "circle-radius": 4.2,
      "circle-color": "#ffffff",
      "circle-stroke-color": "#333333",
      "circle-stroke-width": 1,
    },
  });

  // 5. Labels
  vectorLayers.push({
    id: "vm-labels",
    type: "symbol",
    source: "vectormap",
    filter: [
      "all",
      ["any", ["==", ["geometry-type"], "Point"], ["==", ["geometry-type"], "MultiPoint"]],
      ["==", ["get", "_isLabel"], true],
      ["!", ["has", "reportingPointType"]],
      ["!", ["has", "navaidType"]],
      ["!=", ["get", "mountainPass"], true],
      ["!", ["has", "miscSymbol"]],
      ["!", ["all", ["==", ["get", "_objType"], 11], ["has", "rotation"]]],
    ],
    layout: {
      "icon-image": ["case", ["==", ["get", "_objType"], 14], "runway-label-box", "nav-label-box"],
      "icon-text-fit": "both",
      "icon-text-fit-padding": [0, 1, 0, 1],
      "icon-allow-overlap": false,
      "text-field": ["get", "text"],
      "text-anchor": [
        "case",
        ["==", ["slice", ["coalesce", ["get", "text"], ""], 0, 4], "IAP "],
        "top",
        ["==", ["get", "fatoLabel"], true],
        "top",
        "center",
      ],
      "text-offset": [
        "case",
        ["==", ["slice", ["coalesce", ["get", "text"], ""], 0, 4], "IAP "],
        ["literal", [0, 0.6]],
        ["==", ["get", "fatoLabel"], true],
        ["literal", [0, 0.6]],
        ["literal", [0, 0]],
      ],
      "text-size": [
        "interpolate",
        ["linear"],
        ["zoom"],
        12,
        [
          "case",
          ["==", ["slice", ["coalesce", ["get", "text"], ""], 0, 4], "IAP "],
          18,
          ["==", ["get", "_objType"], 14],
          9,
          11,
        ],
        16,
        [
          "case",
          ["==", ["slice", ["coalesce", ["get", "text"], ""], 0, 4], "IAP "],
          18,
          ["==", ["get", "_objType"], 14],
          11,
          13,
        ],
        19,
        [
          "case",
          ["==", ["slice", ["coalesce", ["get", "text"], ""], 0, 4], "IAP "],
          18,
          ["==", ["get", "_objType"], 14],
          12,
          15,
        ],
      ],
      "text-allow-overlap": false,
      "text-padding": 2,
      "text-max-width": 18,
      // Lower sort keys get collision priority: runway labels before taxiways.
      "symbol-sort-key": ["match", ["get", "_objType"], 11, 0, 13, 1, 2],
      "text-font": ["Libre Franklin Regular"],
    },
    paint: {
      "text-color": [
        "case",
        ["any", ["==", ["get", "fatoLabel"], true], ["==", ["slice", ["coalesce", ["get", "text"], ""], 0, 4], "IAP "]],
        "#ffffff",
        ["==", ["get", "_objType"], 14],
        "#6b7280",
        "#facc15",
      ],
      // Thicken taxiway lettering using the installed glyphs.
      "text-halo-color": "#facc15",
      "text-halo-width": ["case", ["==", ["get", "_objType"], 13], 0.5, 0],
    },
  });
  vectorLayers.push({
    id: "vm-runway-labels",
    type: "symbol",
    source: "vectormap",
    filter: ["all", ["==", ["get", "_objType"], 11], ["has", "text"], ["has", "rotation"]],
    layout: {
      "icon-image": "runway-label-box",
      "icon-text-fit": "both",
      "icon-text-fit-padding": [0, 1, 0, 1],
      "icon-rotation-alignment": "map",
      "text-rotation-alignment": "map",
      "icon-pitch-alignment": "map",
      "text-pitch-alignment": "map",
      "icon-rotate": ["-", ["%", ["+", ["%", ["+", ["get", "rotation"], 90], 180], 180], 180], 90],
      "text-rotate": ["-", ["%", ["+", ["%", ["+", ["get", "rotation"], 90], 180], 180], 180], 90],
      "text-field": ["get", "text"],
      "text-anchor": ["case", ["==", ["get", "thresholdLabel"], true], ["get", "thresholdAnchor"], "center"],
      "text-offset": [
        "case",
        ["==", ["get", "thresholdLabel"], true],
        ["get", "thresholdOffset"],
        ["literal", [0, 2.2]],
      ],
      "symbol-sort-key": ["-", 0, ["coalesce", ["get", "runwayLengthMeters"], 0]],
      "text-size": ["case", ["==", ["get", "thresholdLabel"], true], 18, 13],
      "text-max-width": 40,
      "text-font": ["Libre Franklin Regular"],
      // Runway identification must remain visible in crowded airport diagrams.
      // Both the text and its fitted background must bypass collision rejection.
      "text-allow-overlap": true,
      "icon-allow-overlap": true,
      "text-padding": 2,
    },
    paint: { "text-color": "#000", "text-halo-color": "#000", "text-halo-width": 0.5 },
  });

  // Procedures draw first so opaque airport surfaces cover them at close zoom.
  for (const layer of vectorLayers) {
    if (layer.type === "symbol") continue; // Procedure labels use their dedicated path layer.
    addLayer({
      ...layer,
      id: `${layer.id}-procedures`,
      filter: ["all", ["==", ["get", "_objType"], 15], (layer.filter as ExpressionSpecification) ?? true],
    });
  }
  addLayer({
    id: "vm-procedure-path-labels",
    type: "symbol",
    source: "vectormap",
    filter: [
      "all",
      ["==", ["get", "_objType"], 15],
      ["any", ["==", ["geometry-type"], "LineString"], ["==", ["geometry-type"], "MultiLineString"]],
      [
        "any",
        ["!=", ["coalesce", ["get", "circuitAltitudeLabel"], ""], ""],
        ["!=", ["coalesce", ["get", "procedureName"], ""], ""],
      ],
    ],
    layout: {
      "symbol-placement": "line",
      "symbol-spacing": 350,
      "text-field": [
        "case",
        ["!=", ["coalesce", ["get", "circuitAltitudeLabel"], ""], ""],
        ["get", "circuitAltitudeLabel"],
        ["coalesce", ["get", "procedureName"], ""],
      ],
      "text-font": [
        "case",
        ["!=", ["coalesce", ["get", "circuitAltitudeLabel"], ""], ""],
        ["literal", ["Open Sans Extra Bold"]],
        ["literal", ["Open Sans Semibold"]],
      ],
      "text-size": 22,
      "text-offset": [0, -0.8],
      "text-max-angle": 30,
      "text-rotation-alignment": "map",
      "text-keep-upright": true,
      "text-allow-overlap": false,
      "text-padding": 8,
    },
    paint: {
      "text-color": ["coalesce", ["get", "strokeColor"], "#00008b"],
      "text-halo-color": "#ffffff",
      "text-halo-width": 0.5,
    },
  });
  for (const layer of vectorLayers) {
    if (layer.id === "vm-airspace-halo") continue;
    const airspacePaint = layer.id === "vm-airspace-inner-stroke";
    const genericBoundary = layer.id === "vm-line" || layer.id === "vm-line-dashed";
    map.addLayer({
      ...layer,
      ...(airspacePaint ? { minzoom: labelMinZoom } : {}),
      filter: [
        "all",
        ["!=", ["get", "_objType"], 15],
        ["any", ["!=", ["get", "_objType"], 6], [">=", ["zoom"], OBSTACLE_MIN_ZOOM]],
        ...(genericBoundary ? [["!=", ["get", "_objType"], 0]] : []),
        ...(layer.id === "vm-polygon-fill"
          ? [
              ["any", ["!=", ["get", "_objType"], 0], [">=", ["zoom"], labelMinZoom]],
              ["!=", ["get", "_objType"], 4],
            ]
          : []),
        // Overlays are not chart content, and their geometry only exists BELOW the chart
        // cutoff: webcams (4) and weather (5) stop at resolution 20, which is zoom 12.97,
        // while the cutoff is 13. A layer `minzoom` of labelMinZoom therefore hides them
        // at every zoom, so the cutoff has to be per feature.
        ["any", ["in", ["get", "_objType"], ["literal", OVERLAY_OBJ_TYPES]], [">=", ["zoom"], labelMinZoom]],
        (layer.filter as ExpressionSpecification) ?? true,
      ] as unknown as ExpressionSpecification,
    });
  }
  addLayer({
    id: "vm-airport-buildings",
    type: "fill-extrusion",
    source: "vectormap",
    filter: ["all", ["==", ["get", "_objType"], 14], ["has", "airportBuildingHeightM"]],
    paint: {
      "fill-extrusion-color": ["coalesce", ["get", "fillColor"], "#808080"],
      "fill-extrusion-base": 0,
      "fill-extrusion-height": ["get", "airportBuildingHeightM"],
      "fill-extrusion-opacity": 1,
    },
  });
  // Draw the buildings before airport labels so roofs cannot paint over names.
  const firstVectorSymbol = map
    .getStyle()
    .layers.find((layer) => layer.type === "symbol" && layer.source === "vectormap");
  if (firstVectorSymbol) map.moveLayer("vm-airport-buildings", firstVectorSymbol.id);
  addLayer({
    id: "vm-airspace-labels",
    type: "symbol",
    source: "airspace-labels",
    minzoom: labelMinZoom,
    filter: ["==", ["geometry-type"], "Point"],
    layout: {
      "icon-image": ["get", "icon"],
      "icon-rotate": ["get", "rotation"],
      "icon-rotation-alignment": "viewport",
      "icon-pitch-alignment": "viewport",
      "icon-allow-overlap": false,
      "icon-padding": 4,
      "symbol-sort-key": ["get", "priority"],
    },
  });
  addLayer({
    id: "vm-airspace-curved-labels",
    type: "symbol",
    source: "airspace-labels",
    minzoom: labelMinZoom,
    filter: ["==", ["geometry-type"], "LineString"],
    layout: {
      "symbol-placement": "line-center",
      "text-rotation-alignment": "map",
      "text-keep-upright": true,
      "text-field": [
        "case",
        ["!=", ["get", "badgeIcon"], ""],
        ["format", ["image", ["get", "badgeIcon"]], {}, ["concat", " ", ["get", "limits"]], {}],
        ["format", ["get", "limits"], {}],
      ],
      "text-font": ["Open Sans Regular"],
      "text-size": ["get", "fontSize"],
      "text-max-angle": 35,
      "text-padding": 2,
      "text-allow-overlap": false,
      "symbol-sort-key": ["get", "priority"],
    },
    paint: {
      "text-color": ["get", "textColor"],
      "text-halo-color": ["get", "backgroundColor"],
      "text-halo-width": 3,
      "text-halo-blur": 0.5,
    },
  });
  // The halo covers the label box, with the sharp boundary above both.
  const airspaceHalo = vectorLayers.find((layer) => layer.id === "vm-airspace-halo");
  if (airspaceHalo) addLayer({ ...airspaceHalo, minzoom: labelMinZoom });
  // Redraw only airspace boundaries above their label boxes.
  for (const dashed of [false, true]) {
    addLayer({
      id: dashed ? "vm-airspace-boundary-over-labels-dashed" : "vm-airspace-boundary-over-labels",
      type: "line",
      source: "vectormap",
      minzoom: labelMinZoom,
      filter: ["all", ["==", ["get", "_objType"], 0], dashed ? ["has", "lineDash"] : ["!", ["has", "lineDash"]]],
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": ["coalesce", ["get", "strokeColor"], "#000"],
        "line-width": ["coalesce", ["get", "strokeWidth"], 1],
        ...(dashed ? { "line-dasharray": ["get", "lineDash"] as ExpressionSpecification } : {}),
      },
    });
  }
}

// --------------- overlay sources & layers ---------------

// Layers of GeoJSON sources that are often empty. MapLibre draws the
// tile clipping mask of a source before it looks for data, once per tile and,
// under 3D terrain, once per terrain tile. These four sources are usually
// empty and took 70 % of all mask passes in a 3D profile, so their layers hide
// while the source holds no features.
// Hide EVERY layer of such a source. A visible layer keeps the source loaded,
// and a hidden fill or line layer of a loaded source then never stores its
// terrain texture fingerprint: MapLibre re-renders every terrain tile on every
// frame (maplibre-gl 5.21, RenderToTexture.prepareForRender).
const HIDDEN_WHEN_EMPTY: Record<string, string[]> = {
  "route-planned": ["route-planned-line"],
  "route-active": ["route-active-line"],
  track: ["track-line"],
  "control-features": ["control-features-fill", "control-features-line", "control-features-circle"],
};

/** `setData` on a GeoJSON source; hides the layers above while it is empty. */
export function setGeoJsonData(map: MlMap, sourceId: string, data: GeoJSON.FeatureCollection): void {
  (map.getSource(sourceId) as GeoJSONSource | undefined)?.setData(data);
  const visibility = data.features.length ? "visible" : "none";
  for (const id of HIDDEN_WHEN_EMPTY[sourceId] ?? []) {
    if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", visibility);
  }
}

export function addOverlaySourcesAndLayers(map: MlMap): Promise<boolean> {
  const empty = emptyFC();

  // 6 GeoJSON sources
  map.addSource("control-features", { type: "geojson", data: empty });
  map.addSource("route-active", { type: "geojson", data: empty });
  map.addSource("route-planned", { type: "geojson", data: empty });
  map.addSource("route-points", { type: "geojson", data: empty });
  map.addSource("traffic", { type: "geojson", data: empty });

  // 1. control-features-fill
  map.addLayer({
    id: "control-features-fill",
    type: "fill",
    layout: { visibility: "none" }, // setGeoJsonData shows it once the source has data
    source: "control-features",
    filter: ["any", ["==", ["geometry-type"], "Polygon"], ["==", ["geometry-type"], "MultiPolygon"]],
    paint: {
      "fill-color": ["coalesce", ["get", "fillColor"], "rgba(255,255,0,0.3)"],
      "fill-opacity": 1,
    },
  });

  // 2. control-features-line
  map.addLayer({
    id: "control-features-line",
    type: "line",
    layout: { visibility: "none" }, // setGeoJsonData shows it once the source has data
    source: "control-features",
    filter: [
      "any",
      ["==", ["geometry-type"], "LineString"],
      ["==", ["geometry-type"], "MultiLineString"],
      ["==", ["geometry-type"], "Polygon"],
      ["==", ["geometry-type"], "MultiPolygon"],
    ],
    paint: {
      "line-color": ["coalesce", ["get", "strokeColor"], "rgba(255,0,255,1)"],
      "line-width": ["coalesce", ["get", "strokeWidth"], 4],
    },
  });

  // 3. control-features-circle
  map.addLayer({
    id: "control-features-circle",
    type: "circle",
    layout: { visibility: "none" }, // setGeoJsonData shows it once the source has data
    source: "control-features",
    filter: ["any", ["==", ["geometry-type"], "Point"], ["==", ["geometry-type"], "MultiPoint"]],
    paint: {
      "circle-radius": 3,
      "circle-color": "rgba(255,255,0,1)",
      "circle-stroke-color": "rgba(255,255,0,1)",
      "circle-stroke-width": 2,
    },
  });

  // 4. route-planned-line
  map.addLayer({
    id: "route-planned-line",
    type: "line",
    layout: { visibility: "none" }, // setGeoJsonData shows it once the source has data
    source: "route-planned",
    paint: {
      "line-color": "rgba(255,0,255,0.4)",
      "line-width": 15,
    },
  });

  // 5. route-active-line
  map.addLayer({
    id: "route-active-line",
    type: "line",
    layout: { visibility: "none" }, // setGeoJsonData shows it once the source has data
    source: "route-active",
    paint: {
      "line-color": "rgba(255,0,255,1)",
      "line-width": 5,
    },
  });

  // 6. route-points-circle
  map.addLayer({
    id: "route-points-circle",
    type: "circle",
    source: "route-points",
    paint: {
      "circle-radius": ["coalesce", ["get", "radius"], 4],
      "circle-color": ["coalesce", ["get", "fillColor"], "rgba(255,255,255,1)"],
      "circle-stroke-color": ["coalesce", ["get", "strokeColor"], "rgba(255,255,255,1)"],
      "circle-stroke-width": 2,
    },
  });
  // 7. route-points-label
  map.addLayer({
    id: "route-points-label",
    type: "symbol",
    source: "route-points",
    layout: {
      "text-field": ["get", "displayName"],
      "text-size": 14,
      "text-offset": [0, 2.2],
      "symbol-sort-key": ["-", 0, ["coalesce", ["get", "runwayLengthMeters"], 0]],
      "text-allow-overlap": true,
      "text-font": ["Libre Franklin Bold"],
    },
    paint: {
      "text-color": "rgba(255,0,255,1)",
      "text-halo-color": "rgba(0,0,0,0.5)",
      "text-halo-width": 5,
      "text-halo-blur": 0,
    },
  });

  // 8. traffic-circle
  map.addLayer({
    id: "traffic-circle",
    type: "circle",
    source: "traffic",
    paint: {
      "circle-radius": ["coalesce", ["get", "radius"], 12],
      "circle-color": "rgba(0,0,0,0)",
      "circle-stroke-color": ["coalesce", ["get", "color"], "#ffcc33"],
      "circle-stroke-width": 6,
    },
  });

  // 9. traffic-label
  map.addLayer({
    id: "traffic-label",
    type: "symbol",
    source: "traffic",
    layout: {
      "text-field": ["get", "description"],
      "text-size": 14,
      "text-offset": [0, 2],
      "text-allow-overlap": true,
      "text-font": ["Libre Franklin bold"],
    },
    paint: {
      "text-color": "#fff",
      "text-halo-color": "#f00",
      "text-halo-width": 3,
      "text-halo-blur": 0,
    },
  });

  // 10. track source + layer
  map.addSource("track", { type: "geojson", data: empty });
  map.addLayer({
    id: "track-line",
    type: "line",
    layout: { visibility: "none" }, // setGeoJsonData shows it once the source has data
    source: "track",
    paint: {
      "line-color": "rgba(139,69,19,1)",
      "line-width": 6,
    },
  });

  // 11. aircraft source + layers
  map.addSource("aircraft", { type: "geojson", data: empty });

  map.addLayer({
    id: "aircraft-lines",
    type: "line",
    source: "aircraft",
    filter: ["==", ["geometry-type"], "LineString"],
    paint: {
      "line-color": "rgba(139,69,19,1)",
      "line-width": 6,
    },
  });

  map.addLayer({
    id: "aircraft-symbol",
    type: "symbol",
    source: "aircraft",
    filter: ["==", ["geometry-type"], "Point"],
    layout: {
      "icon-image": "aircraft-icon",
      "icon-size": 0.18,
      "icon-rotate": ["get", "heading"],
      "icon-rotation-alignment": "map",
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
    },
  });

  // Load aircraft icon — resolve with loaded status
  return map
    .loadImage(environment.aircraftIcon)
    .then((img: any) => {
      if (map && img) {
        map.addImage("aircraft-icon", img.data);
        return true;
      }
      return false;
    })
    .catch((error: any) => {
      console.error("[WebglMapV2Service] Failed to load aircraft icon", error);
      return false;
    });
}
